import React, { useState } from "react";
import PropTypes from "prop-types";
import { graphql, withPrefix } from "gatsby";
import { Helmet } from "react-helmet";
import {
  FaBolt,
  FaProjectDiagram,
  FaClipboardCheck,
  FaUsers,
  FaShieldAlt,
  FaRocket,
} from "react-icons/fa";

import Content, { HTMLContent } from "../components/Content";
import "./landing-funnel-page.sass";

export const LandingFunnelPageTemplate = ({
  title,
  description,
  heroTitle,
  heroSubtitle,
  heroImage,
  primaryCta,
  secondaryCta,
  benefits,
  sections,
  offerTypes,
  trustStrip,
  leadCapture,
  testimonials,
  faqs,
  content,
  contentComponent,
}) => {
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const PostContent = contentComponent || Content;
  const heroImageSrc =
    heroImage && heroImage.childImageSharp
      ? heroImage.childImageSharp.fluid.src
      : heroImage && heroImage.publicURL
      ? withPrefix(heroImage.publicURL)
      : typeof heroImage === "string"
      ? heroImage.startsWith("/")
        ? withPrefix(heroImage)
        : heroImage
      : null;
  const activeSections = (sections || []).filter(
    (item) => item && item.enabled !== false,
  );
  const activeOfferTypes = (offerTypes || []).filter(
    (item) => item && item.enabled !== false,
  );
  const hasLeadCapture = !!(leadCapture && leadCapture.enabled);
  const formMode =
    leadCapture && leadCapture.provider ? leadCapture.provider : "netlify";
  const formActionUrl =
    (leadCapture &&
      (leadCapture.formActionUrl || leadCapture.mailchimpActionUrl)) ||
    "";
  const formTitle =
    (leadCapture && leadCapture.title) ||
    "Reserva tu cupo al workshop gratuito";
  const formDescription =
    (leadCapture && leadCapture.description) ||
    "Workshop en vivo de 2 horas sobre un caso real de arquitectura de software.";
  const searchParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : null;
  const utmSource = searchParams ? searchParams.get("utm_source") || "" : "";
  const utmMedium = searchParams ? searchParams.get("utm_medium") || "" : "";
  const utmCampaign = searchParams
    ? searchParams.get("utm_campaign") || ""
    : "";
  const primaryCtaMode =
    primaryCta && primaryCta.behavior ? primaryCta.behavior : "link";
  const isPrimaryModalCta = primaryCtaMode === "modal" && hasLeadCapture;
  const isExternalPrimaryCta =
    primaryCta &&
    typeof primaryCta.url === "string" &&
    /^https?:\/\//.test(primaryCta.url);
  const openPrimaryInNewTab =
    primaryCta && typeof primaryCta.openInNewTab === "boolean"
      ? primaryCta.openInNewTab
      : isExternalPrimaryCta;
  const showInlineLeadCapture = hasLeadCapture && !isPrimaryModalCta;
  const trustItemCatalog = {
    aplicacion_inmediata: {
      label: "Aplicacion inmediata",
      icon: FaBolt,
    },
    frameworks_decision: {
      label: "Frameworks de decision",
      icon: FaProjectDiagram,
    },
    casos_reales: {
      label: "Casos reales",
      icon: FaClipboardCheck,
    },
    mentoria_grupal: {
      label: "Mentoria grupal",
      icon: FaUsers,
    },
    enfoque_practico: {
      label: "Enfoque practico",
      icon: FaRocket,
    },
    calidad_arquitectonica: {
      label: "Calidad arquitectonica",
      icon: FaShieldAlt,
    },
  };
  const fallbackTrustStrip = [
    "aplicacion_inmediata",
    "frameworks_decision",
    "casos_reales",
  ];
  const activeTrustStrip =
    trustStrip && trustStrip.length ? trustStrip : fallbackTrustStrip;

  return (
    <main className="landing-funnel-page">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <section className="funnel-shell">
        <div className="funnel-hero">
          <div className="funnel-hero-copy">
            <p className="funnel-kicker">
              Programa premium para arquitectos de software
            </p>
            <h1>{heroTitle || title}</h1>
            <p className="funnel-subtitle">{heroSubtitle || description}</p>

            <div className="funnel-cta-group">
              {isPrimaryModalCta ? (
                <button
                  type="button"
                  className="funnel-btn funnel-btn-primary"
                  onClick={() => setIsLeadModalOpen(true)}
                >
                  {primaryCta.text || "Reservar cupo"}
                </button>
              ) : primaryCta && primaryCta.url ? (
                <a
                  className="funnel-btn funnel-btn-primary"
                  href={primaryCta.url}
                  target={openPrimaryInNewTab ? "_blank" : undefined}
                  rel={openPrimaryInNewTab ? "noopener noreferrer" : undefined}
                >
                  {primaryCta.text || "Ver curso"}
                </a>
              ) : null}
              {secondaryCta && secondaryCta.url ? (
                <a
                  className="funnel-btn funnel-btn-secondary"
                  href={secondaryCta.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {secondaryCta.text || "Ver mas"}
                </a>
              ) : null}
            </div>

            <div className="funnel-trust-strip">
              {activeTrustStrip.map((itemKey) => {
                const trustItem = trustItemCatalog[itemKey];
                if (!trustItem) {
                  return null;
                }

                const TrustIcon = trustItem.icon;

                return (
                  <span key={itemKey} className="funnel-trust-item">
                    <TrustIcon aria-hidden="true" />
                    <span>{trustItem.label}</span>
                  </span>
                );
              })}
            </div>
          </div>

          {heroImageSrc ? (
            <div className="funnel-hero-media">
              <img src={heroImageSrc} alt={heroTitle || title} />
            </div>
          ) : null}
        </div>
      </section>

      {isPrimaryModalCta ? (
        <div
          className={`funnel-modal-overlay ${isLeadModalOpen ? "is-open" : ""}`}
          onClick={() => setIsLeadModalOpen(false)}
          aria-hidden={!isLeadModalOpen}
        >
          <div
            className="funnel-modal"
            role="dialog"
            aria-modal="true"
            aria-label={formTitle}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="funnel-modal-close"
              onClick={() => setIsLeadModalOpen(false)}
              aria-label="Cerrar"
            >
              x
            </button>
            <h3>{formTitle}</h3>
            <p className="funnel-lead-copy">{formDescription}</p>

            {formMode === "mailchimp" && formActionUrl ? (
              <form
                className="funnel-lead-form"
                action={formActionUrl}
                method="post"
                target="_blank"
                rel="noopener noreferrer"
              >
                <input type="hidden" name="UTM_SOURCE" value={utmSource} />
                <input type="hidden" name="UTM_MEDIUM" value={utmMedium} />
                <input type="hidden" name="UTM_CAMPAIGN" value={utmCampaign} />
                <input
                  type="text"
                  name="FNAME"
                  placeholder="Tu nombre"
                  required
                />
                <input type="text" name="ROLE" placeholder="Tu rol" required />
                <input
                  type="email"
                  name="EMAIL"
                  placeholder="Tu email"
                  required
                />
                <button type="submit" className="funnel-btn funnel-btn-primary">
                  {leadCapture.buttonText || "Reservar cupo"}
                </button>
              </form>
            ) : (
              <form
                className="funnel-lead-form"
                name={leadCapture.formName || "workshop-leads"}
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
              >
                <input
                  type="hidden"
                  name="form-name"
                  value={leadCapture.formName || "workshop-leads"}
                />
                <input type="hidden" name="utm_source" value={utmSource} />
                <input type="hidden" name="utm_medium" value={utmMedium} />
                <input type="hidden" name="utm_campaign" value={utmCampaign} />
                <p className="is-hidden">
                  <label>
                    No llenar: <input name="bot-field" />
                  </label>
                </p>
                <input
                  type="text"
                  name="name"
                  placeholder="Tu nombre"
                  required
                />
                <input type="text" name="role" placeholder="Tu rol" required />
                <input
                  type="email"
                  name="email"
                  placeholder="Tu email"
                  required
                />
                <button type="submit" className="funnel-btn funnel-btn-primary">
                  {leadCapture.buttonText || "Reservar cupo"}
                </button>
              </form>
            )}
          </div>
        </div>
      ) : null}

      <section className="funnel-shell funnel-grid-2">
        {benefits && benefits.length ? (
          <div className="funnel-panel">
            <h3>Lo que te llevas</h3>
            <ul className="funnel-list">
              {benefits.map((item, index) => (
                <li key={`benefit-${index}`}>{item.benefit}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {activeSections.length ? (
          <div className="funnel-panel">
            <h3>Ruta del programa</h3>
            <div className="funnel-stack">
              {activeSections.map((item, index) => (
                <article key={`section-${index}`} className="funnel-item">
                  {item.heading ? <h4>{item.heading}</h4> : null}
                  {item.body ? <p>{item.body}</p> : null}
                </article>
              ))}
            </div>
          </div>
        ) : null}

        {activeOfferTypes.length ? (
          <div className="funnel-panel">
            <h3>Tipos de oferta</h3>
            <ul className="funnel-list">
              {activeOfferTypes.map((offer, index) => (
                <li key={`offer-${index}`}>{offer.type}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      {showInlineLeadCapture ? (
        <section id="reserva-cupo" className="funnel-shell">
          <div className="funnel-panel funnel-lead-panel">
            <h3>{formTitle}</h3>
            <p className="funnel-lead-copy">{formDescription}</p>

            {formMode === "mailchimp" && formActionUrl ? (
              <form
                className="funnel-lead-form"
                action={formActionUrl}
                method="post"
                target="_blank"
                rel="noopener noreferrer"
              >
                <input type="hidden" name="UTM_SOURCE" value={utmSource} />
                <input type="hidden" name="UTM_MEDIUM" value={utmMedium} />
                <input type="hidden" name="UTM_CAMPAIGN" value={utmCampaign} />
                <input
                  type="text"
                  name="FNAME"
                  placeholder="Tu nombre"
                  required
                />
                <input type="text" name="ROLE" placeholder="Tu rol" required />
                <input
                  type="email"
                  name="EMAIL"
                  placeholder="Tu email"
                  required
                />
                <button type="submit" className="funnel-btn funnel-btn-primary">
                  {leadCapture.buttonText || "Reservar cupo"}
                </button>
              </form>
            ) : (
              <form
                className="funnel-lead-form"
                name={leadCapture.formName || "workshop-leads"}
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
              >
                <input
                  type="hidden"
                  name="form-name"
                  value={leadCapture.formName || "workshop-leads"}
                />
                <input type="hidden" name="utm_source" value={utmSource} />
                <input type="hidden" name="utm_medium" value={utmMedium} />
                <input type="hidden" name="utm_campaign" value={utmCampaign} />
                <p className="is-hidden">
                  <label>
                    No llenar: <input name="bot-field" />
                  </label>
                </p>
                <input
                  type="text"
                  name="name"
                  placeholder="Tu nombre"
                  required
                />
                <input type="text" name="role" placeholder="Tu rol" required />
                <input
                  type="email"
                  name="email"
                  placeholder="Tu email"
                  required
                />
                <button type="submit" className="funnel-btn funnel-btn-primary">
                  {leadCapture.buttonText || "Reservar cupo"}
                </button>
              </form>
            )}
          </div>
        </section>
      ) : null}

      {testimonials && testimonials.length ? (
        <section className="funnel-shell">
          <div className="funnel-panel">
            <h3>Testimonios</h3>
            <div className="funnel-testimonials">
              {testimonials.map((item, index) => (
                <article key={`testimonial-${index}`} className="funnel-quote">
                  <p>{item.quote}</p>
                  <p className="funnel-quote-author">
                    {item.name}
                    {item.role ? ` - ${item.role}` : ""}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {faqs && faqs.length ? (
        <section className="funnel-shell">
          <div className="funnel-panel">
            <h3>Preguntas frecuentes</h3>
            <div className="funnel-faqs">
              {faqs.map((item, index) => (
                <details key={`faq-${index}`} className="funnel-faq-item">
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="funnel-shell">
        <div className="funnel-panel funnel-content-panel">
          <PostContent content={content} />
        </div>
      </section>
    </main>
  );
};

LandingFunnelPageTemplate.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  heroTitle: PropTypes.string,
  heroSubtitle: PropTypes.string,
  heroImage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  primaryCta: PropTypes.object,
  secondaryCta: PropTypes.object,
  benefits: PropTypes.array,
  sections: PropTypes.array,
  offerTypes: PropTypes.array,
  trustStrip: PropTypes.array,
  leadCapture: PropTypes.object,
  testimonials: PropTypes.array,
  faqs: PropTypes.array,
  content: PropTypes.node,
  contentComponent: PropTypes.func,
};

const LandingFunnelPage = ({ data }) => {
  const { markdownRemark: post } = data;

  return (
    <LandingFunnelPageTemplate
      title={post.frontmatter.title}
      description={post.frontmatter.description}
      heroTitle={post.frontmatter.heroTitle}
      heroSubtitle={post.frontmatter.heroSubtitle}
      heroImage={post.frontmatter.heroImage}
      primaryCta={post.frontmatter.primaryCta}
      secondaryCta={post.frontmatter.secondaryCta}
      benefits={post.frontmatter.benefits}
      sections={post.frontmatter.sections}
      offerTypes={post.frontmatter.offerTypes}
      trustStrip={post.frontmatter.trustStrip}
      leadCapture={post.frontmatter.leadCapture}
      testimonials={post.frontmatter.testimonials}
      faqs={post.frontmatter.faqs}
      content={post.html}
      contentComponent={HTMLContent}
    />
  );
};

LandingFunnelPage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object,
  }),
};

export default LandingFunnelPage;

export const pageQuery = graphql`
  query LandingFunnelByID($id: String!) {
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
        description
        heroTitle
        heroSubtitle
        heroImage
        primaryCta {
          text
          url
          behavior
          openInNewTab
        }
        secondaryCta {
          text
          url
        }
        benefits {
          benefit
        }
        sections {
          heading
          body
          enabled
        }
        leadCapture {
          enabled
          provider
          title
          description
          formName
          buttonText
          mailchimpActionUrl
          formActionUrl
        }
        testimonials {
          name
          role
          quote
        }
        faqs {
          question
          answer
        }
        offerTypes {
          type
          enabled
        }
        trustStrip
      }
    }
  }
`;
